import { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import { supabase } from "@/lib/supabaseClient";
import ModuleGate from "@/components/base/ModuleGate";
import UploadField from "@/components/base/UploadField";
import { deleteFileFromVps } from "@/lib/vpsStorage";
import VpsUploadField from "@/components/base/VpsUploadField";
import type { Job } from "@/pages/admin/jobs/components/JobModal";

type PackageFile = {
  id: string;
  tenant_id: string;
  media_package_id: string;
  file_type: string;
  title: string;
  url: string;
  thumbnail_url: string | null;
  sort_order: number;
  created_at: string;
};

type MediaPackage = {
  id: string;
  tenant_id: string;
  job_id: string;
  client_id: string;
  package_type: string;
  title: string;
  cover_url: string | null;
  frame_url: string | null;
  video_url: string | null;
  download_url: string | null;
  usb_label: string | null;
  notes: string | null;
  is_published: boolean;
  public_token: string;
  view_count: number;
  download_count: number;
  created_at: string;
  updated_at: string;
};

const PACKAGE_TYPES = [
  { value: "online_dvd", label: "Online DVD", icon: "ri-disc-line" },
  { value: "usb", label: "USB Package", icon: "ri-usb-line" },
  { value: "embed", label: "Embed Player", icon: "ri-play-circle-line" },
  { value: "cover_frame", label: "Cover / Frame", icon: "ri-image-edit-line" },
  { value: "combo", label: "Combo Pack", icon: "ri-gift-2-line" },
];

const TYPE_STYLES: Record<string, string> = {
  online_dvd:  "bg-violet-50 text-violet-600",
  usb:         "bg-amber-50 text-amber-600",
  embed:       "bg-sky-50 text-sky-600",
  cover_frame: "bg-emerald-50 text-emerald-600",
  combo:       "bg-[#C8A96E]/10 text-[#a6853e]",
};

const TYPE_BGS: Record<string, string> = {
  online_dvd:  "bg-violet-50",
  usb:         "bg-amber-50",
  embed:       "bg-sky-50",
  cover_frame: "bg-emerald-50",
  combo:       "bg-[#C8A96E]/8",
};

const FILE_TYPE_STYLES: Record<string, { icon: string; label: string; color: string }> = {
  video:       { icon: "ri-video-line", label: "Video", color: "text-sky-600" },
  photo:       { icon: "ri-image-line", label: "Photo", color: "text-emerald-600" },
  download:    { icon: "ri-download-line", label: "Download", color: "text-amber-600" },
  cover:       { icon: "ri-image-edit-line", label: "Cover", color: "text-violet-600" },
  other:       { icon: "ri-file-3-line", label: "File", color: "text-slate-500" },
};

const EMPTY_FORM = { package_type: "online_dvd", title: "", cover_url: "", frame_url: "", video_url: "", download_url: "", usb_label: "", notes: "" };
type FormState = typeof EMPTY_FORM;

interface Props {
  job: Job;
  tenantId: string;
}

export default function MediaPackageTab({ job, tenantId }: Props) {
  const [packages, setPackages] = useState<MediaPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPkg, setEditingPkg] = useState<MediaPackage | null>(null);
  const [form, setForm] = useState<FormState>({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [expandedPkgId, setExpandedPkgId] = useState<string | null>(null);
  const [pkgFiles, setPkgFiles] = useState<Record<string, PackageFile[]>>();
  const [filesLoading, setFilesLoading] = useState<string | null>(null);
  const [newFile, setNewFile] = useState({ title: "", url: "", file_type: "other" });
  const [addingFileFor, setAddingFileFor] = useState<string | null>(null);

  const fetchPackages = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("media_packages")
      .select("*")
      .eq("job_id", job.id)
      .eq("tenant_id", tenantId)
      .order("created_at", { ascending: false });
    setPackages((data ?? []) as MediaPackage[]);
    setLoading(false);
  }, [job.id, tenantId]);

  useEffect(() => { fetchPackages(); }, [fetchPackages]);

  const openNew = () => { setEditingPkg(null); setForm({ ...EMPTY_FORM }); setShowForm(true); };
  const openEdit = (p: MediaPackage) => {
    setEditingPkg(p);
    setForm({ package_type: p.package_type, title: p.title, cover_url: p.cover_url ?? "", frame_url: p.frame_url ?? "", video_url: p.video_url ?? "", download_url: p.download_url ?? "", usb_label: p.usb_label ?? "", notes: p.notes ?? "" });
    setShowForm(true);
  };
  const cancelForm = () => { setShowForm(false); setEditingPkg(null); };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    const payload = {
      package_type: form.package_type, title: form.title,
      cover_url: form.cover_url || null, frame_url: form.frame_url || null,
      video_url: form.video_url || null, download_url: form.download_url || null,
      usb_label: form.usb_label || null, notes: form.notes || null,
    };
    if (editingPkg) {
      const { data } = await supabase.from("media_packages").update({ ...payload, updated_at: new Date().toISOString() }).eq("id", editingPkg.id).eq("tenant_id", tenantId).select().maybeSingle();
      if (data) setPackages((prev) => prev.map((p) => (p.id === editingPkg.id ? (data as MediaPackage) : p)));
    } else {
      const token = Math.random().toString(36).slice(2, 14);
      const { data } = await supabase.from("media_packages").insert({
        ...payload, tenant_id: tenantId, job_id: job.id, client_id: job.client_id,
        is_published: false, public_token: token,
      }).select().maybeSingle();
      if (data) setPackages((prev) => [data as MediaPackage, ...prev]);
    }
    setSaving(false);
    cancelForm();
  };

  const handleTogglePublish = async (p: MediaPackage) => {
    setBusyId(p.id);
    const { data } = await supabase.from("media_packages")
      .update({ is_published: !p.is_published, updated_at: new Date().toISOString() })
      .eq("id", p.id).eq("tenant_id", tenantId).select().maybeSingle();
    if (data) setPackages((prev) => prev.map((x) => (x.id === p.id ? (data as MediaPackage) : x)));
    setBusyId(null);
  };

  const handleDelete = async (p: MediaPackage) => {
    const result = await Swal.fire({
      title: "Delete Package?", text: `"${p.title}" will be removed.`, icon: "warning",
      showCancelButton: true, confirmButtonText: "Delete", cancelButtonText: "Cancel",
      confirmButtonColor: "#ef4444", cancelButtonColor: "#374151",
    });
    if (!result.isConfirmed) return;
    setBusyId(p.id);

    // Delete associated files from VPS
    const urlsToDelete = [p.cover_url, p.frame_url, p.video_url, p.download_url].filter(Boolean) as string[];
    for (const url of urlsToDelete) {
      const { data: fileRecord } = await supabase
        .from("uploaded_files")
        .select("id")
        .eq("file_url", url)
        .eq("tenant_id", tenantId)
        .maybeSingle();
      if (fileRecord?.id) {
        try {
          await deleteFileFromVps({ fileId: fileRecord.id, tenantId, silent: true });
        } catch {
          // Continue
        }
      }
    }

    const { error } = await supabase.from("media_packages").delete().eq("id", p.id).eq("tenant_id", tenantId);
    if (!error) setPackages((prev) => prev.filter((x) => x.id !== p.id));
    setBusyId(null);
  };

  const copyPublicLink = async (token: string) => {
    const url = `${window.location.origin}/media/${token}`;
    await navigator.clipboard.writeText(url);
    Swal.fire({ title: "Copied!", text: "Public link copied to clipboard.", icon: "success", timer: 1500, showConfirmButton: false });
  };

  const openPublicLink = (token: string) => {
    window.open(`/media/${token}`, "_blank", "noopener,noreferrer");
  };

  const typeLabel = (type: string) => PACKAGE_TYPES.find((t) => t.value === type)?.label ?? type;
  const typeIcon = (type: string) => PACKAGE_TYPES.find((t) => t.value === type)?.icon ?? "ri-disc-line";

  /* ── Package Files (multi-file) ── */
  const toggleExpand = async (pkgId: string) => {
    if (expandedPkgId === pkgId) {
      setExpandedPkgId(null);
      return;
    }
    setExpandedPkgId(pkgId);
    if (!pkgFiles[pkgId]) {
      setFilesLoading(pkgId);
      const { data } = await supabase
        .from("media_package_files")
        .select("*")
        .eq("media_package_id", pkgId)
        .eq("tenant_id", tenantId)
        .order("sort_order", { ascending: true });
      setPkgFiles((prev) => ({ ...prev, [pkgId]: (data ?? []) as PackageFile[] }));
      setFilesLoading(null);
    }
  };

  const addFile = async (pkgId: string) => {
    if (!newFile.url.trim()) return;
    const { data } = await supabase
      .from("media_package_files")
      .insert({
        tenant_id: tenantId,
        media_package_id: pkgId,
        title: newFile.title.trim() || newFile.file_type,
        url: newFile.url.trim(),
        file_type: newFile.file_type,
        sort_order: (pkgFiles[pkgId]?.length ?? 0),
      })
      .select()
      .maybeSingle();
    if (data) {
      setPkgFiles((prev) => ({
        ...prev,
        [pkgId]: [...(prev[pkgId] ?? []), data as PackageFile],
      }));
    }
    setNewFile({ title: "", url: "", file_type: "other" });
    setAddingFileFor(null);
  };

  const removeFile = async (pkgId: string, fileId: string) => {
    const result = await Swal.fire({
      title: "Remove file?", text: "This file will be removed from this package.", icon: "warning",
      showCancelButton: true, confirmButtonText: "Remove", cancelButtonText: "Cancel",
      confirmButtonColor: "#ef4444", cancelButtonColor: "#374151",
    });
    if (!result.isConfirmed) return;

    // Delete from VPS if file exists
    const file = pkgFiles[pkgId]?.find((f) => f.id === fileId);
    if (file?.url) {
      const { data: fileRecord } = await supabase
        .from("uploaded_files")
        .select("id")
        .eq("file_url", file.url)
        .eq("tenant_id", tenantId)
        .maybeSingle();
      if (fileRecord?.id) {
        try {
          await deleteFileFromVps({ fileId: fileRecord.id, tenantId, silent: true });
        } catch {
          // Continue
        }
      }
    }

    await supabase.from("media_package_files").delete().eq("id", fileId).eq("tenant_id", tenantId);
    setPkgFiles((prev) => ({
      ...prev,
      [pkgId]: (prev[pkgId] ?? []).filter((f) => f.id !== fileId),
    }));
  };

  return (
    <ModuleGate moduleId="media_packages" moduleName="Media Packages / DVD Online">
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-xs text-slate-400">
            <span>{packages.length} packages</span>
            <span className="text-emerald-600">{packages.filter((p) => p.is_published).length} published</span>
          </div>
          {!showForm && (
            <button onClick={openNew} className="flex items-center gap-1.5 bg-[#C8A96E] hover:bg-[#b8965a] text-black text-sm font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm"></i></span>
              New Package
            </button>
          )}
        </div>

        {/* Form */}
        {showForm && (
          <form onSubmit={handleSave} className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-[#0f172a] font-medium text-sm">{editingPkg ? "Edit Package" : "New Media Package"}</p>
              <button type="button" onClick={cancelForm} className="text-slate-300 hover:text-slate-500 cursor-pointer transition-colors">
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-close-line"></i></span>
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-slate-400 text-xs mb-1.5 block uppercase tracking-wider">Type</label>
                <select value={form.package_type} onChange={(e) => setForm((p) => ({ ...p, package_type: e.target.value }))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/50 transition-colors cursor-pointer">
                  {PACKAGE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-slate-400 text-xs mb-1.5 block uppercase tracking-wider">Title *</label>
                <input type="text" value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                  required placeholder="Wedding DVD Package" className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <UploadField label="Cover Image" value={form.cover_url} onChange={(url) => setForm((p) => ({ ...p, cover_url: url }))} accept="image/*" hint="Package cover" urlPlaceholder="https://..." showPreview tenantId={tenantId} entityType="media_package" entityId={editingPkg?.id || ""} folder="media" maxSizeMB={10} />
              <UploadField label="Frame / Template" value={form.frame_url} onChange={(url) => setForm((p) => ({ ...p, frame_url: url }))} accept="image/*" hint="DVD/USB frame design" urlPlaceholder="https://..." showPreview tenantId={tenantId} entityType="media_package" entityId={editingPkg?.id || ""} folder="media" maxSizeMB={10} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <UploadField label="Video URL" value={form.video_url} onChange={(url) => setForm((p) => ({ ...p, video_url: url }))} accept="video/*" hint="Direct video or streaming link" urlPlaceholder="https://vimeo.com/..." tenantId={tenantId} entityType="media_package" entityId={editingPkg?.id || ""} folder="media" maxSizeMB={500} />
              <UploadField label="Download URL" value={form.download_url} onChange={(url) => setForm((p) => ({ ...p, download_url: url }))} accept="*/*" hint="ZIP or download link" urlPlaceholder="https://drive.google.com/..." tenantId={tenantId} entityType="media_package" entityId={editingPkg?.id || ""} folder="media" maxSizeMB={2000} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-slate-400 text-xs mb-1.5 block uppercase tracking-wider">USB Label</label>
                <input type="text" value={form.usb_label} onChange={(e) => setForm((p) => ({ ...p, usb_label: e.target.value }))}
                  placeholder="Johnson Wedding 2024" className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors" />
              </div>
              <div>
                <label className="text-slate-400 text-xs mb-1.5 block uppercase tracking-wider">Notes</label>
                <input type="text" value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                  placeholder="Internal notes..." className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors" />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={cancelForm} className="px-4 py-2 text-sm text-slate-500 hover:text-[#0f172a] border border-slate-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap">Cancel</button>
              <button type="submit" disabled={saving} className="flex items-center gap-1.5 px-5 py-2 bg-[#C8A96E] hover:bg-[#b8965a] text-black text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50">
                {saving ? <><i className="ri-loader-4-line animate-spin text-sm"></i>Saving...</> : "Save Package"}
              </button>
            </div>
          </form>
        )}

        {/* List */}
        {loading ? (
          <div className="space-y-3">{[1, 2].map((i) => <div key={i} className="h-24 bg-slate-50 rounded-xl animate-pulse" />)}</div>
        ) : packages.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 bg-white rounded-xl border border-slate-200">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-slate-100">
              <span className="w-5 h-5 flex items-center justify-center text-slate-400"><i className="ri-disc-line text-2xl"></i></span>
            </div>
            <p className="text-slate-400 text-sm">No media packages yet</p>
            <button onClick={openNew} className="text-xs text-[#C8A96E] hover:text-[#a6853e] cursor-pointer transition-colors">Create first package &rarr;</button>
          </div>
        ) : (
          <div className="space-y-4">
            {packages.map((p) => {
              const isBusy = busyId === p.id;
              const isExpanded = expandedPkgId === p.id;
              const files = pkgFiles[p.id] ?? [];
              return (
                <div key={p.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="flex items-start gap-4 p-5">
                    {/* Cover */}
                    <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-slate-100 flex items-center justify-center">
                      {p.cover_url ? (
                        <img src={p.cover_url} alt={p.title} className="w-full h-full object-cover object-top" />
                      ) : (
                        <div className={`w-full h-full flex items-center justify-center ${TYPE_BGS[p.package_type] ?? "bg-slate-100"}`}>
                          <span className="w-5 h-5 flex items-center justify-center"><i className={`${typeIcon(p.package_type)} text-lg ${TYPE_STYLES[p.package_type]?.split(" ").pop() ?? "text-slate-400"}`}></i></span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div>
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <p className="text-[#0f172a] font-semibold text-sm">{p.title}</p>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${TYPE_STYLES[p.package_type] ?? "bg-slate-100 text-slate-400"}`}>{typeLabel(p.package_type)}</span>
                            {p.is_published ? (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 whitespace-nowrap flex items-center gap-1">
                                <span className="w-3 h-3 flex items-center justify-center"><i className="ri-global-line text-[10px]"></i></span>
                                Published
                              </span>
                            ) : (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-400 whitespace-nowrap">Draft</span>
                            )}
                          </div>
                          {p.notes && <p className="text-slate-400 text-xs">{p.notes}</p>}
                          {/* Analytics */}
                          <div className="flex items-center gap-3 mt-1.5">
                            <span className="text-[10px] text-slate-400 flex items-center gap-1">
                              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-eye-line"></i></span>
                              {p.view_count} views
                            </span>
                            <span className="text-[10px] text-slate-400 flex items-center gap-1">
                              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-download-line"></i></span>
                              {p.download_count} downloads
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {isBusy ? (
                            <div className="w-4 h-4 border-2 border-[#C8A96E] border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <>
                              {p.is_published && (
                                <>
                                  <button onClick={() => copyPublicLink(p.public_token)} title="Copy public link" className="w-7 h-7 flex items-center justify-center text-slate-300 hover:text-[#C8A96E] hover:bg-[#C8A96E]/10 rounded-lg transition-all cursor-pointer">
                                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-link-m text-sm"></i></span>
                                  </button>
                                  <button onClick={() => openPublicLink(p.public_token)} title="Open public link" className="w-7 h-7 flex items-center justify-center text-slate-300 hover:text-[#2563eb] hover:bg-blue-50 rounded-lg transition-all cursor-pointer">
                                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-external-link-line text-sm"></i></span>
                                  </button>
                                </>
                              )}
                              <button onClick={() => openEdit(p)} className="w-7 h-7 flex items-center justify-center text-slate-300 hover:text-[#0f172a] hover:bg-slate-100 rounded-lg transition-all cursor-pointer" title="Edit">
                                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-pencil-line text-sm"></i></span>
                              </button>
                              <button onClick={() => handleTogglePublish(p)} className={`w-7 h-7 flex items-center justify-center rounded-lg transition-all cursor-pointer ${p.is_published ? "text-slate-300 hover:text-amber-600 hover:bg-amber-50" : "text-emerald-500 hover:text-emerald-600 hover:bg-emerald-50"}`} title={p.is_published ? "Unpublish" : "Publish"}>
                                <span className="w-4 h-4 flex items-center justify-center"><i className={`text-sm ${p.is_published ? "ri-eye-off-line" : "ri-global-line"}`}></i></span>
                              </button>
                              <button onClick={() => handleDelete(p)} className="w-7 h-7 flex items-center justify-center text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all cursor-pointer" title="Delete">
                                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-delete-bin-line text-sm"></i></span>
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      {/* Links row */}
                      <div className="flex items-center gap-3 flex-wrap">
                        {p.video_url && <a href={p.video_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-slate-400 hover:text-sky-500 transition-colors"><span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-video-line text-xs"></i></span>Video</a>}
                        {p.download_url && <a href={p.download_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-slate-400 hover:text-[#C8A96E] transition-colors"><span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-download-line text-xs"></i></span>Download</a>}
                        {p.usb_label && <span className="flex items-center gap-1 text-xs text-slate-400"><span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-usb-line text-xs"></i></span>{p.usb_label}</span>}
                        {p.is_published && (
                          <span className="text-xs text-slate-300 font-mono truncate max-w-[200px]">/media/{p.public_token}</span>
                        )}
                        <button onClick={() => toggleExpand(p.id)} className="text-xs text-[#C8A96E] hover:text-[#a6853e] cursor-pointer flex items-center gap-1 transition-colors ml-auto">
                          <span className="w-3.5 h-3.5 flex items-center justify-center"><i className={`${isExpanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-xs`}></i></span>
                          {files.length} files
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Expanded files section */}
                  {isExpanded && (
                    <div className="border-t border-slate-100 px-5 py-4 bg-slate-50/50">
                      <p className="text-slate-500 text-xs font-medium mb-3 uppercase tracking-wider">Package Files</p>
                      {filesLoading === p.id ? (
                        <div className="space-y-2">{[1, 2].map((i) => <div key={i} className="h-10 bg-slate-100 rounded-lg animate-pulse" />)}</div>
                      ) : files.length === 0 ? (
                        <p className="text-slate-400 text-xs py-2">No files added yet. Add a file below.</p>
                      ) : (
                        <div className="space-y-2 mb-3">
                          {files.map((f) => {
                            const ft = FILE_TYPE_STYLES[f.file_type] ?? FILE_TYPE_STYLES.other;
                            return (
                              <div key={f.id} className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg px-3 py-2">
                                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 shrink-0">
                                  <i className={`${ft.icon} text-sm ${ft.color}`}></i>
                                </span>
                                <div className="flex-1 min-w-0">
                                  <p className="text-[#0f172a] text-xs font-medium truncate">{f.title}</p>
                                  <p className="text-slate-300 text-[10px] font-mono truncate">{f.url}</p>
                                </div>
                                <a href={f.url} target="_blank" rel="noopener noreferrer" className="text-slate-300 hover:text-[#2563eb] transition-colors text-xs">
                                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-external-link-line"></i></span>
                                </a>
                                <button onClick={() => removeFile(p.id, f.id)} className="text-slate-300 hover:text-red-500 transition-colors text-xs">
                                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-close-line"></i></span>
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Add file form */}
                      {addingFileFor === p.id ? (
                        <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-2">
                          <div className="grid grid-cols-3 gap-2">
                            <input
                              type="text" value={newFile.title} onChange={(e) => setNewFile((f) => ({ ...f, title: e.target.value }))}
                              placeholder="File title" className="col-span-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50"
                            />
                            <select value={newFile.file_type} onChange={(e) => setNewFile((f) => ({ ...f, file_type: e.target.value }))}
                              className="col-span-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-[#0f172a] outline-none focus:border-[#C8A96E]/50 cursor-pointer"
                            >
                              <option value="other">Other</option>
                              <option value="video">Video</option>
                              <option value="photo">Photo</option>
                              <option value="download">Download</option>
                              <option value="cover">Cover</option>
                            </select>
                            <input
                              type="text" value={newFile.url} onChange={(e) => setNewFile((f) => ({ ...f, url: e.target.value }))}
                              placeholder="https://..." className="col-span-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50"
                            />
                          </div>
                          <div className="space-y-2">
                            <VpsUploadField
                              value={newFile.url || null}
                              onChange={(url) => setNewFile((f) => ({ ...f, url: url ?? "" }))}
                              tenantId={tenantId}
                              entityType="media_package_file"
                              entityId={p.id}
                              label="Upload file"
                              accept="*/*"
                              showPreview={false}
                              helperText="Upload or paste URL above"
                              folder="media"
                            />
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => setAddingFileFor(null)} className="px-3 py-1.5 text-xs text-slate-500 border border-slate-200 rounded-lg cursor-pointer whitespace-nowrap">Cancel</button>
                            <button onClick={() => addFile(p.id)} disabled={!newFile.url.trim()} className="px-3 py-1.5 text-xs font-semibold bg-[#C8A96E] hover:bg-[#b8965a] text-black rounded-lg cursor-pointer whitespace-nowrap disabled:opacity-50">
                              Add File
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button onClick={() => { setAddingFileFor(p.id); setNewFile({ title: "", url: "", file_type: "other" }); }} className="text-xs text-[#C8A96E] hover:text-[#a6853e] cursor-pointer flex items-center gap-1 transition-colors">
                          <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-add-line text-xs"></i></span>
                          Add file
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </ModuleGate>
  );
}